import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CUSTOMIZATION_MENU_ITEMS, ICustomizationMenuItem } from '@models/customization.types';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { CustomizationCustomFieldsComponent } from './customization-custom-fields/customization-custom-fields.component';
import { CustomizationCustomInfoComponent } from './customization-custom-info/customization-custom-info.component';

export { CustomizationContentType, ICustomizationMenuItem } from '@models/customization.types';

@Component({
    selector: 'app-customization',
    templateUrl: './customization.component.html',
    styleUrls: ['./customization.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RouterModule,
        SectionHeaderComponent,
        CustomizationCustomFieldsComponent,
        CustomizationCustomInfoComponent
    ]
})
export class CustomizationComponent {
    readonly menuItems = CUSTOMIZATION_MENU_ITEMS;
    readonly selectedPath = signal<string>(CUSTOMIZATION_MENU_ITEMS[0].path);
    readonly selectedItem = signal<ICustomizationMenuItem>(CUSTOMIZATION_MENU_ITEMS[0]);

    readonly isFieldsView = computed(() => this.selectedItem().contentType === 'customFields');
    readonly hasUnsavedChanges = signal<boolean>(false);

    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly routeParams = toSignal(this.route.params, { initialValue: {} });

    constructor() {
        effect(() => {
            const params = this.routeParams();
            const entity = (params && 'entity' in params ? params['entity'] : null) as string | null;
            this.setSelectedPath(entity);
        });
    }

    onNavigate(path: string): void {
        if (this.hasUnsavedChanges()) return;
        this.router.navigate(['/configuration', 'customization', path]);
    }

    onUnsavedChangesChange(hasChanges: boolean): void {
        this.hasUnsavedChanges.set(hasChanges);
    }

    private setSelectedPath(path: string | null): void {
        const defaultTab = this.menuItems[0];
        if (!path) {
            this.navigateToDefaultTab(defaultTab);
            return;
        }

        const found = this.menuItems.find((item) => item.path === path);
        if (!found) {
            this.navigateToDefaultTab(defaultTab);
            return;
        }

        this.selectedPath.set(found.path);
        this.selectedItem.set(found);
    }

    private navigateToDefaultTab(defaultTab: ICustomizationMenuItem): void {
        this.router.navigate(['/configuration', 'customization', defaultTab.path]);
        this.selectedPath.set(defaultTab.path);
        this.selectedItem.set(defaultTab);
    }
}
